//@flow
import React, {useState} from 'react';
import {
  makeStyles,
  Typography,
  FormControlLabel,
  Hidden,
  Link,
} from '@material-ui/core';
import {Delete} from '@material-ui/icons';

import {colors} from '../../../../../../assets/styles/colors';
import {useBannerActions} from '../../../../utils';
import type {MainBanner} from '../../../../../../entities';
import {getImageUrl} from '../../../../../../utils/image_provider';
import ConfirmModal from '../../../../../../elements/Modals/ConfirmModal';
import YoutubeIcon from '../../../../../../elements/icons/YoutubeIcon';
import IosSwitch from '../../../../../../elements/IosSwitch/IosSwitch';
import {useStoreDomain} from '../../../../../store/utils';
import {getBannerActionUrl} from '../utils';

type Props = {
  banner: MainBanner,
  featureDisabled?: boolean,
  onEdit?: () => void,
  onDelete: () => void,
  onToggleEnabled: () => void,
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    gap: '.5rem',
    padding: '.75rem 0',
    position: 'relative',
    flexDirection: 'column',
    width: '100%',
    '&[data-disabled="true"]': {
      opacity: '.5',
    },

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  thumbnail: {
    cursor: 'pointer',
    height: '80px',
    width: '100px',
    minWidth: '100px',
    minHeight: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '3rem',
    borderRadius: '.75rem',
    overflow: 'hidden',
    color: colors.primary,
    background: `${colors.primary}1c`,

    '& img': {
      height: '100%',
      width: '100%',
      objectFit: 'cover',
      transition: '.3s transform ease-in-out',
    },

    '& img:hover': {
      transform: 'scale(1.1)',
    },
  },
  data: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    overflow: 'hidden',
  },
  title: {
    fontSize: '14px',
    fontWeight: '500',
  },
  subtitle: {
    fontSize: '14px',
  },
  subtitleDisabled: {
    color: colors.warning,
    fontWeight: 600,
    fontSize: '14px',
  },

  actions: {
    // position: 'absolute',
    bottom: 0,
    height: '100%',
    right: '.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: '1rem',
    paddingBlock: '.5rem',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-end',
      gap: '.5rem',
    },
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '.25rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '.25rem',
    borderRadius: '99px',

    '&:hover': {
      '&[data-action="edit"]': {
        background: `${colors.primary}1c`,
      },

      '&[data-action="delete"]': {
        background: `${colors.error}1c`,
      },
    },
  },
  editIconButton: {
    color: colors.primary,
    background: `${colors.primary}26`,
    borderRadius: '50%',
    padding: '.25rem',
  },
  deleteIconButton: {
    color: colors.error,
    background: `${colors.error}26`,
    borderRadius: '50%',
    padding: '.25rem',
  },
  editLabelButton: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: colors.primary,
  },
  deleteLabelButton: {
    fontSize: '14px',
    fontWeight: '500',
  },
  switchLabel: {
    fontSize: '14px',
  },
  bannerData: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flex: 1,
    [theme.breakpoints.up('lg')]: {
      width: '70%',
    },
  },
  action: {
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
}));

export default function BannerItem(props: Props) {
  const {banner, onDelete, featureDisabled, onToggleEnabled} = props;

  const classes = useStyles();
  const {openBanner} = useBannerActions();
  const {domain} = useStoreDomain();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);

  const {imageId, title, subtitle, videoUrl} = banner;

  const image = getImageUrl(imageId);

  const bannerMediaType = videoUrl ? 'VIDEO' : 'IMAGE';
  const actionsUrl = getBannerActionUrl({
    identifier: banner.buttonEntityIdentifier,
    type: banner.bannerButtonEntityType,
    domain: domain.domainName,
  });
  return (
    <>
      <div className={classes.container}>
        <div className={classes.bannerData}>
          <div
            onClick={() => {
              openBanner(banner);
            }}
            role="button"
            data-disabled={!banner.enabled}
          >
            <picture className={classes.thumbnail}>
              {bannerMediaType === 'VIDEO' ? (
                <YoutubeIcon />
              ) : (
                <img
                  src={image}
                  alt={`Banner que lleva como titulo ${title}`}
                />
              )}
            </picture>
          </div>

          <div className={classes.data}>
            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.subtitle}>{subtitle}</Typography>
            <Typography className={classes.subtitle}>
              Botón: {banner.buttonText}
            </Typography>
            <Typography className={classes.action}>
              Acción:{' '}
              <Link target="_blank" href={actionsUrl}>
                {actionsUrl}
              </Link>
            </Typography>
            {featureDisabled && (
              <Typography className={classes.subtitleDisabled}>
                ⚠ El banner no se muestra en la tienda
              </Typography>
            )}
          </div>
        </div>

        <Hidden smDown>
          <footer
            className={classes.actions}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <FormControlLabel
              labelPlacement="start"
              control={
                <div className={classes.switch}>
                  <IosSwitch
                    checked={banner.enabled}
                    onChange={onToggleEnabled}
                  />
                </div>
              }
              classes={{
                label: classes.switchLabel,
              }}
              label={banner.enabled ? 'Habilitado' : 'Deshabilitado'}
            />

            <button
              className={classes.actionButton}
              data-action="delete"
              onClick={() => {
                openConfirmModal();
              }}
            >
              <span className={classes.deleteLabelButton}>Eliminar</span>
              <Delete className={classes.deleteIconButton} />
            </button>
          </footer>
        </Hidden>

        <Hidden mdUp>
          <footer
            className={classes.actions}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <FormControlLabel
              labelPlacement="end"
              control={
                <div className={classes.switch}>
                  <IosSwitch
                    checked={banner.enabled}
                    onChange={onToggleEnabled}
                  />
                </div>
              }
              classes={{
                label: classes.switchLabel,
              }}
              label={banner.enabled ? 'Habilitado' : 'Deshabilitado'}
            />

            <button
              className={classes.actionButton}
              data-action="delete"
              onClick={() => {
                openConfirmModal();
              }}
            >
              <Delete className={classes.deleteIconButton} />
              <span className={classes.deleteLabelButton}>Eliminar</span>
            </button>
          </footer>
        </Hidden>
      </div>

      <ConfirmModal
        open={confirmModalOpen}
        title="¿Estás seguro de eliminar este banner?"
        message="Esta acción no se puede deshacer"
        onConfirm={() => {
          onDelete();
          closeConfirmModal();
        }}
        onClose={() => closeConfirmModal()}
        onCancel={() => closeConfirmModal()}
      />
    </>
  );
}
